import type { AppShellNavGroup, AppShellNavItem } from '@easydoctor/ui';
import type { Role } from '@easyhelp/shared';
import {
  IconArchive,
  IconChartBar,
  IconHelp,
  IconInbox,
  IconMailQuestion,
  IconSettings,
  IconUserCircle,
  type Icon,
} from '@tabler/icons-react';
import { createElement } from 'react';

/** Contatori delle voci, letti dall'API: assenti finché non arrivano (nessun badge). */
export interface NavCounts {
  /** Ticket aperti, per la Coda (#104). */
  openTickets?: number;
  /** Email della posta non associata in attesa (#42). */
  unassociatedMail?: number;
}

interface NavEntry {
  id: string;
  label: string;
  href: string;
  icon: Icon;
  /** Se presente, la voce compare solo a questi ruoli (DEC-006). */
  roles?: readonly Role[];
  /** Altri percorsi in cui la voce è la sezione corrente (es. il dettaglio di un ticket per la Coda). */
  activePrefixes?: readonly string[];
  /** Contatore accanto all'etichetta e testo che il lettore di schermo aggiunge al nome del link. */
  counter?: { key: keyof NavCounts; label: (count: number) => string };
}

/** Voci del gruppo «Supporto» del modulo Assistenza, nell'ordine del prototipo. */
const SUPPORT_ENTRIES: readonly NavEntry[] = [
  {
    id: 'coda',
    label: 'Coda',
    href: '/',
    icon: IconInbox,
    activePrefixes: ['/ticket/'],
    counter: { key: 'openTickets', label: (count) => `${count} ${count === 1 ? 'aperto' : 'aperti'}` },
  },
  // Archivio (#123): Risolto, Chiuso e Scartato. Nessun badge (decisione del PO del 16/10/2026): il
  // badge segnala lavoro che aspetta, e l'archivio non lo è; il totale è sotto il titolo della pagina.
  { id: 'archivio', label: 'Archivio', href: '/archivio', icon: IconArchive },
  // Riservata al team, cioè a ogni ruolo (ADR-003): Analisti e Sviluppo.
  {
    id: 'posta-non-associata',
    label: 'Posta non associata',
    href: '/posta-non-associata',
    icon: IconMailQuestion,
    counter: { key: 'unassociatedMail', label: (count) => `${count} in attesa` },
  },
  // Statistiche (#11): riservate all'amministratore (decisione del PO del 02/10/2026).
  { id: 'statistiche', label: 'Statistiche', href: '/statistiche', icon: IconChartBar, roles: ['amministratore'] },
  // Guida (#57): il manuale degli operatori, per ogni ruolo (PRD-006 US-1).
  { id: 'guida', label: 'Guida', href: '/guida', icon: IconHelp },
];

/** Voci in fondo alla sidebar, dopo il divisore (come in EasyCare). */
const FOOTER_ENTRIES: readonly NavEntry[] = [
  // Profilo (#152): per ogni ruolo. Il menu del chip utente di `AppShell` ha solo «Esci», quindi la
  // voce sta qui finché `@easydoctor/ui` non permette altre voci in quel menu.
  { id: 'profilo', label: 'Profilo', href: '/profilo', icon: IconUserCircle },
  { id: 'impostazioni', label: 'Impostazioni', href: '/impostazioni', icon: IconSettings, roles: ['amministratore'] },
];

function visibleTo(role: Role | undefined) {
  return (entry: NavEntry) => !entry.roles || (role !== undefined && entry.roles.includes(role));
}

function toNavItem({ id, label, href, icon, activePrefixes, counter }: NavEntry, counts: NavCounts): AppShellNavItem {
  return {
    id,
    label,
    href,
    icon: createElement(icon),
    activePrefixes,
    ...(counter && { badge: counts[counter.key], badgeLabel: counter.label }),
  };
}

/** Le voci principali visibili a un ruolo, con i contatori. */
export function navFor(role: Role | undefined, counts: NavCounts = {}): AppShellNavGroup[] {
  return [
    {
      id: 'supporto',
      title: 'Supporto',
      items: SUPPORT_ENTRIES.filter(visibleTo(role)).map((entry) => toNavItem(entry, counts)),
    },
  ];
}

/** Le voci in fondo alla sidebar visibili a un ruolo. */
export function footerNavFor(role: Role | undefined): AppShellNavItem[] {
  return FOOTER_ENTRIES.filter(visibleTo(role)).map((entry) => toNavItem(entry, {}));
}

/** Le pagine che usano tutta la larghezza: coda e archivio, anche con il dettaglio aperto sopra (PAGE-001, #123). */
export function isWidePage(pathname: string): boolean {
  return (
    pathname === '/' || pathname.startsWith('/ticket/') || pathname === '/archivio' || pathname.startsWith('/archivio/')
  );
}
